import React, { useState } from 'react';

interface ColorPickerProps {
  label: string;
  value: string;
  onChange: (color: string) => void;
  description?: string;
  className?: string;
}

const ColorPicker: React.FC<ColorPickerProps> = ({
  label,
  value,
  onChange,
  description,
  className = ''
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [isValidColor, setIsValidColor] = useState(true);

  const validateColor = (color: string): boolean => {
    return /^#[0-9A-F]{6}$/i.test(color);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    const isValid = validateColor(newValue);
    setIsValidColor(isValid);
    
    if (isValid) {
      onChange(newValue);
    }
  };

  const handleColorPickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const color = e.target.value;
    setInputValue(color);
    setIsValidColor(true);
    onChange(color);
  };

  const commonColors = [
    '#4F46E5', '#7C3AED', '#059669', '#0EA5E9', '#DC2626', '#EA580C',
    '#CA8A04', '#65A30D', '#0891B2', '#7C2D12', '#1F2937', '#374151'
  ];

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      
      <div className="flex items-center space-x-3">
        {/* Color Picker Input */}
        <div className="relative">
          <input
            type="color"
            value={inputValue}
            onChange={handleColorPickerChange}
            className="w-12 h-10 border border-gray-300 rounded-md cursor-pointer"
            title="Pick a color"
          />
          <div 
            className="absolute inset-0 rounded-md border-2 border-white shadow-sm pointer-events-none"
            style={{ backgroundColor: isValidColor ? inputValue : '#cccccc' }}
          />
        </div>

        {/* Text Input */}
        <div className="flex-1">
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            placeholder="#4F46E5"
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              isValidColor 
                ? 'border-gray-300 focus:border-blue-500' 
                : 'border-red-300 focus:border-red-500 focus:ring-red-500'
            }`}
          />
          {!isValidColor && (
            <p className="mt-1 text-xs text-red-600">
              Please enter a valid hex color (e.g., #4F46E5)
            </p>
          )}
        </div>
      </div>

      {/* Color Preview */}
      <div className="flex items-center space-x-2">
        <div 
          className="w-8 h-8 rounded border border-gray-300 shadow-sm"
          style={{ backgroundColor: isValidColor ? inputValue : '#cccccc' }}
          title={`Preview: ${inputValue}`}
        />
        <span className="text-sm text-gray-500">Preview</span>
      </div>

      {/* Common Colors */}
      <div className="space-y-2">
        <p className="text-xs text-gray-500">Quick colors:</p>
        <div className="flex flex-wrap gap-2">
          {commonColors.map((color) => (
            <button
              key={color}
              type="button"
              onClick={() => {
                setInputValue(color);
                setIsValidColor(true);
                onChange(color);
              }}
              className={`w-8 h-8 rounded border-2 hover:scale-110 transition-transform ${
                inputValue === color ? 'border-gray-800' : 'border-gray-300'
              }`}
              style={{ backgroundColor: color }}
              title={color}
            />
          ))}
        </div>
      </div>

      {description && (
        <p className="text-xs text-gray-500">{description}</p>
      )}
    </div>
  );
};

export default ColorPicker;